/* eslint-disable no-use-before-define */
import React, { useEffect, useState, useRef } from 'react';
import './Intake.css';
import PropTypes from 'prop-types';
import app from 'new-ui/app';
import arrowDownIcon from 'assets/icons/arrow_down.svg';
import checkboxDoneIcon from 'assets/icons/checkbox1.svg';
import editIcon from 'assets/icons/edit_2.svg';
import { useTranslation } from 'utils/modifiedTranslation';
import classNames from 'classnames';
import {
  capitalizeFirstLetter,
  getBiomarkerTitle,
  hasGenderQuestion,
} from 'new-ui/functions';
import { getUserCondition } from 'modules/user/utils';
import { render } from 'react-dom';
import { isArray } from 'lodash';
import { ROUTE_PATH, ROUTE_NAME } from 'utils/constants/router';
import { GA_INTAKE_CATEGORY, GA_INTAKE_EVENTS } from 'modules/ga/generalEvents';
import { INTAKE_GA } from 'modules/ga/intakeEvents';
import { getQuestions } from 'new-ui/functions/getQuestions';
import { useIsMounted } from 'utils/hooks/useIsMounted';
import { Preloader } from 'components/Preloader';
import {
  ALL_MULTI_PAGES_QUESTIONS,
  MULTI_PAGES_QUESTIONS_TITLE,
  QUESTION_TYPE,
} from 'new-ui/Components/QuestionRenderer/constants';
import { QuestionRenderer } from 'new-ui/Components/QuestionRenderer/QuestionRenderer';
import { getPropertyTitle } from 'new-ui/Components/QuestionRenderer/renderers/MultiPages';
import { getProfileTitles } from './api';
import {
  CUSTOM_INTAKE_QUESTIONS,
  INTAKE_ABOUT_QUESTION,
  INTAKE_QUESTIONS,
  INTAKE_SECTIONS,
  QUESTION_TITLE_TRANSLATION_KEY_BY_QUESTION_ID,
} from './questions';
import { Accordion } from '../Accordion';
import { conditionChecker, getQstack } from './functions';
import Button from '../Button';

const ABOUT_QUESTION_IDS = ['user_type', 'dob', 'gender', 'race'];

export const PRIMARY_THEME = 'primary';
export const SECONDARY_THEME = 'secondary';
const THEMES = [PRIMARY_THEME, SECONDARY_THEME];

export const EXPAND_ALL = 'all';
export const EXPAND_FIRST = 'first';

export const QUESTION_IDS = {
  TREATMENTS: 'treatments',
  PROCEDURES: 'procedures',
  BIOMARKERS: 'biomarkers',
};

const isArrayQuestion = ({ id }) => Object.values(QUESTION_IDS).includes(id);

export const CLIENT_IM_NOT_SURE = 'client_im_not_sure';
const notSureDictionary = JSON.parse(localStorage.getItem(CLIENT_IM_NOT_SURE)) || {};

const intercomUpdated = false;

window._accordions = {};

const isQuestionAnswered = (question, user = app.user) => {
  if (notSureDictionary[question.id]) return true;
  if (question.isQuestionAnswered) return question.isQuestionAnswered(user);
  if (question.id === 'user_type' && user?.info.user_type) return true;
  if (question.id === 'dob' && user?.personal.dob) return true;
  if (question.id === 'gender' && hasGenderQuestion() && user?.personal.gender) return true;
  if (question.id === 'race' && user?.condition_profile.race?.length) return true;
  const v = user.condition_profile[question.id];
  if (v === 0) return true;
  if (Array.isArray(v) && !v.length) return false;
  if (v === null || v === undefined) return false;
  return !!v;
};

const getConditionQuestions = (condition) => ({
  [INTAKE_SECTIONS.ABOUT_YOUR_CANCER]: INTAKE_QUESTIONS[INTAKE_SECTIONS.ABOUT_YOUR_CANCER][condition],
  [INTAKE_SECTIONS.ABOUT_YOUR_TREATMENT]: INTAKE_QUESTIONS[INTAKE_SECTIONS.ABOUT_YOUR_TREATMENT][condition],
  [INTAKE_SECTIONS.ABOUT_YOUR_HEALTH]: INTAKE_QUESTIONS[INTAKE_SECTIONS.ABOUT_YOUR_HEALTH][condition],
  [INTAKE_SECTIONS.ABOUT_YOU]: INTAKE_QUESTIONS[INTAKE_SECTIONS.ABOUT_YOU].all,
});

const questionOpenedEvent = (item) => {
  if (item.collapsed) {
    app.sendGoogleAnalyticsEvent(GA_INTAKE_CATEGORY, `Intake opened "${item.title}"`);
    app.sendGoogleAnalyticsEvent(INTAKE_GA.category, INTAKE_GA.events.treatment_q_viewed);
  }
};

const questionAnsweredEvent = (item) => {
  app.sendGoogleAnalyticsEvent(GA_INTAKE_CATEGORY, `"${item.title}" Intake answered`);
};

const questionPresentedEvent = (items) => {
  const visibleQuestions = items.flatMap((item) => item.items).filter((item) => !item.hidden);
  visibleQuestions.forEach((item) => {
    app.sendGoogleAnalyticsEvent(GA_INTAKE_CATEGORY, `Intake presented "${item.title}"`);
  });
};

export const getIntakeQuestions = (user, settings = {}) => {
  const {
    byId,
    byType,
    isShown,
    isHidden,
    isAnswered,
    isUnanswered,
    fromQid,
    customQuestions,
    appendQuestions,
    questionsData,
    customData,
  } = settings;

  let questions = customData?.questions || JSON.parse(JSON.stringify(app.questions ?? []));

  if (appendQuestions) questions = appendQuestions.concat(questions);

  const ALL_CONDITION_QUESTION = {
    [INTAKE_SECTIONS.ABOUT_YOUR_CANCER]: questions.filter((a) => a.section === 'MY_CANCER').map((a) => a.id),
    [INTAKE_SECTIONS.ABOUT_YOUR_TREATMENT]: questions.filter((a) => a.section === 'TREATMENT').map((a) => a.id),
    [INTAKE_SECTIONS.ABOUT_YOUR_HEALTH]: questions.filter((a) => a.section === 'MY_HEALTH').map((a) => a.id),
    [INTAKE_SECTIONS.ABOUT_YOU]: [],
  };

  let CONDITION_QUESTIONS = customData?.questionsData || getConditionQuestions(user.personal.condition);

  let allQuestions = customData?.allQuestions || questions;
  allQuestions = customData?.allQuestions || CUSTOM_INTAKE_QUESTIONS.concat(allQuestions);

  if (customQuestions) {
    allQuestions = allQuestions.filter((a) => customQuestions.includes(a.id));
    CONDITION_QUESTIONS = ALL_CONDITION_QUESTION;
  }

  if (questionsData) {
    CONDITION_QUESTIONS = {};
    allQuestions = [];
    Object.keys(questionsData).forEach((type) => {
      allQuestions = allQuestions.concat(questionsData[type]);
      CONDITION_QUESTIONS[type] = questionsData[type].map((a) => a.id);
    });
  }

  const types = {};
  const ids = {};

  let sections = customData?.sections || Object.values(INTAKE_SECTIONS);

  if (questionsData) sections = Object.keys(questionsData);

  const questionById = new Map((allQuestions ?? []).map((question) => [question.id, question]));

  let allIntakeQuestions = [];
  sections.forEach((type) => {
    types[type] = [];

    (CONDITION_QUESTIONS[type] ?? []).forEach((questionId) => {
      const question = questionById.get(questionId);
      if (!question) {
        return;
      }
      allIntakeQuestions.push({
        ...question,
        intakeType: type,
      });
    });
  });

  if (fromQid) allIntakeQuestions = allIntakeQuestions.slice(allIntakeQuestions.findIndex((a) => a.id === fromQid));

  allIntakeQuestions.forEach((q) => {
    // eslint-disable-next-line no-param-reassign
    q.isAnswered = isQuestionAnswered(q, user);
    // eslint-disable-next-line no-param-reassign
    q.isShown = q.show ? true : conditionChecker(q, user);
  });

  allIntakeQuestions = allIntakeQuestions.filter((q) => {
    let f = true;
    if (isShown && !q.isShown) f = false;
    if (isHidden && q.isShown) f = false;
    if (isAnswered && !q.isAnswered) f = false;
    if (isUnanswered && q.isAnswered) f = false;
    return f;
  });

  allIntakeQuestions.forEach((q) => {
    types[q.intakeType].push(q);
    ids[q.id] = q;
  });

  if (byId && byType) {
    return {
      ids,
      types,
    };
  }

  if (byId) return ids;
  if (byType) return types;

  return allIntakeQuestions;
};

export const getIntakeUnansweredNum = (user) => {
  if (!app?.questions?.length) {
    // getIntakeQuestions relies on app.questions is set, but some code is calling it before that actually happens
    // eslint-disable-next-line no-console
    console.log('getIntakeUnansweredNum: app.questions is not initialized yet');
    return 0;
  }
  return getIntakeQuestions(user, {
    isShown: true,
    isUnanswered: true,
  }).length;
};

window.getIntakeUnansweredNum = getIntakeUnansweredNum;

const Intake = (props) => {
  const {
    id,
    user,
    showPreloader = true,
    onUpdate,
    gotoResultsAction,
    showOnlyUnanswered = false,
    showOnlyAnswered = false,
    preventNotSure,
    flatMode = false, // no top level type division
    customQuestions,
    appendQuestions,
    expandQuestions,
    customOnSelect,
    questionsData,
    sortSections,
    expand,
    customData,
    onAccordionRender,
    getCustomRenderer,
    onQuestion,
    theme = 'primary',
    customizeItems,
    onInitialized,
  } = props;

  let {
    section,
    qid,
  } = props;

  if (qid === INTAKE_SECTIONS.ABOUT_YOU) {
    qid = null;
    section = INTAKE_SECTIONS.ABOUT_YOU;
  }

  const isMounted = useIsMounted();
  const accordionRef = useRef(null);
  const titlesRef = useRef(null);
  const isInitialized = useRef(false);

  const { t } = useTranslation();

  const condition = getUserCondition(user);

  const [updateCount, setUpdateCount] = useState(0);
  const resetIntake = () => setUpdateCount((prev) => prev + 1);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const create = async () => {
    if (condition) {
      // for SearchForm when condition is not selected we render mock questions
      const { data } = await getProfileTitles(condition);
      titlesRef.current = data;
    }

    // override existing app.questions potentially fetched with additionalParams
    app.questions = await getQuestions(condition);

    const items = [];
    const allQuestions = getIntakeQuestions(user, {
      byType: true,
      customQuestions,
      appendQuestions,
      questionsData,
      customData,
      isAnswered: showOnlyAnswered,
    });

    if (onQuestion) {
      Object.keys(allQuestions).forEach((k) => {
        const qs = allQuestions[k];
        if (isArray(qs)) {
          qs.forEach((q) => {
            onQuestion(q);
          });
        }
      });
    }

    let sections = customData?.sections || Object.values(INTAKE_SECTIONS);

    if (questionsData) sections = Object.keys(questionsData);

    if (sortSections) sections.sort((a) => (sortSections.includes(a) ? -1 : 1));

    const expandAll = expand === EXPAND_ALL;
    const expandFirst = expand === EXPAND_FIRST;
    let isExpanded = false;

    sections.forEach((type) => {
      const questions = allQuestions[type];
      if (!questions?.length) return;

      if (!preventNotSure) {
        questions.forEach((q) => {
          if (q.options && !q.options.find((a) => a.value === 'not_sure')) {
            q.options.push({
              label: "I'm not sure",
              value: CLIENT_IM_NOT_SURE,
              isSingle: true,
            });
          }
        });
      }

      if (showOnlyUnanswered && !questions.find((a) => !a.isAnswered && a.isShown)) return;

      const item = {
        isMain: true,
        type,
        title: t(`intake.section_title_${type}`),
        collapsed: expandAll ? false : (section ? (section !== type) : true),
        className: 'intake-top-header',
        head: AccordionHeader,
      };

      items.push(item);

      const subitems = [];

      questions.forEach((q) => {
        let title = titlesRef.current ? titlesRef.current.en[q.id] : q.id.split('_').join(' ');
        if (q.intakeTitle) {
          title = t(q.intakeTitle);
        }
        if (QUESTION_TITLE_TRANSLATION_KEY_BY_QUESTION_ID[q.id]) {
          title = t(QUESTION_TITLE_TRANSLATION_KEY_BY_QUESTION_ID[q.id]);
        }

        const isAnswered = isQuestionAnswered(q, user);
        if (showOnlyUnanswered && isAnswered) return;
        if (showOnlyAnswered && !isAnswered) return;
        let className = 'intake-item';
        if (q.className) className = ['intake-item', q.className];

        const itemData = {
          name: q.id,
          q,
          title,
          collapsed: !expandAll,
          className,
          label: QuestionLabel,
          head: QuestionHeader,
          body: QuestionBody,
        };

        if (expandFirst && !isExpanded) {
          isExpanded = true;
          itemData.collapsed = false;
          item.collapsed = false;
        }

        subitems.push(itemData);
        item.items = subitems;
      });
    });

    const processedItems = customizeItems ? customizeItems(items) : items;

    accordionRef.current = new Accordion({
      $element: id ? document.querySelector(`.intake-${id}`) : document.querySelector('.intake'),
      onrender: onAccordionRender,
      className: 'intake-accordion',
      items: processedItems,
      onbeforerender: () => {
        const intakeQuestions = getIntakeQuestions(user, {
          byId: true,
          byType: true,
          isShown: true,
          allQuestions,
          customData,
          customQuestions,
          appendQuestions,
          questionsData,
        });

        itemsIterator((item) => {
          if (item.isMain) {
            // eslint-disable-next-line no-param-reassign
            item.counter = [
              intakeQuestions.types[item.type].filter((a) => a.isAnswered).length,
              intakeQuestions.types[item.type].length,
            ];
          } else if (item.q) {
            // eslint-disable-next-line no-param-reassign
            item.hidden = !intakeQuestions.ids[item.q.id];
          }
        }, processedItems);
      },
    });

    questionPresentedEvent(processedItems);

    if (!processedItems.length) {
      document.querySelector('.intake').innerHTML = '';
      render(
        <div className="intake-all-answered">
          <div className="intake-all-answered-title">
            {t('intake.all_questions_done')}
          </div>
          <div className="intake-go-to-results">
            <Button
              title={t('general.back_to_home')}
              action={() => {
                if (gotoResultsAction) {
                  gotoResultsAction();
                  return;
                }
                app.history.push(ROUTE_PATH[ROUTE_NAME.RESULTS]);
              }}
            />
          </div>
        </div>,
        document.querySelector('.intake'),
      );
    }

    // it is used in src/new-ui/Components/HCP/Select/index.js (hack)
    window._accordion = accordionRef.current;
    if (id) window._accordions[id] = accordionRef.current;

    if (!section && !qid && !showOnlyAnswered) {
      showUnanswered();
    } else if (qid) showQuestion(qid);

    if (expandQuestions) expandQuestions.forEach((q) => showQuestion(q));
  };

  useEffect(() => {
    if (!isInitialized.current || updateCount) {
      const isInitialization = !isInitialized.current;
      create().then(() => {
        if (isInitialization && onInitialized && isMounted()) {
          onInitialized();
        }
      });
      isInitialized.current = true;
    }
  }, [create, isMounted, onInitialized, updateCount, user]);

  const itemsIterator = (callback, items = accordionRef.current?.items) => {
    if (!items) return;
    items.forEach((item) => {
      const { items: itemItems } = item;
      callback(item);
      if (!itemItems) {
        return;
      }
      itemsIterator(callback, itemItems);
    });
  };

  const showQuestion = (questionIdToShow) => {
    let targetQuestionId = questionIdToShow;
    if (ABOUT_QUESTION_IDS.includes(qid)) {
      targetQuestionId = INTAKE_ABOUT_QUESTION.id;
    }
    itemsIterator((item) => {
      if (item.isMain) return;
      const question = item.q;
      if (question.id === targetQuestionId) {
        questionOpenedEvent(item);
        if (typeof item.show !== 'function') {
          console.error('showQuestion item.show is not a function', item);
          return;
        }
        item.show();
        item.parent.show();
      }
    });
  };

  const showUnanswered = (questionIdToShow) => {
    const questions = getIntakeQuestions(user, {
      isShown: true,
      isUnanswered: true,
      fromQid: questionIdToShow,
      customData,
      customQuestions,
      appendQuestions,
      questionsData,
    });
    if (questions.length) showQuestion(questions[0].id);
  };

  const getQuestionValue = (question) => {
    if (notSureDictionary[question.id]) return t('intake.im_not_sure');
    if (question.getQuestionValue) return question.getQuestionValue(user);
    const v = user.condition_profile[question.id];
    const getValueLabel = (value) => {
      if (question.type === QUESTION_TYPE.NUMBER_INPUT) {
        return value;
      }
      if (question?.options?.length) {
        const option = question.options.filter((a) => a.value === String(v))[0];
        return t(option?.label);
      }
      return value;
    };
    if (v === null) return '-';
    if (ALL_MULTI_PAGES_QUESTIONS.includes(question.id)) {
      return v.map((item) => getPropertyTitle(item, MULTI_PAGES_QUESTIONS_TITLE[question.id], question, t)).join(', ');
    }
    if (question.id === 'biomarkers' && v?.map) return v.map((b) => getBiomarkerTitle(b)).join(', ');
    if (typeof v === 'object') return v.map(getValueLabel).join(', ');
    if (typeof v === 'string' || typeof v === 'number') return getValueLabel(v);
    return v;
  };

  const getQuestionTitle = (q) => {
    if (q.id === 'biomarkers') return t('intake.biomarkers_title');
    if (q.id === 'treatments') return 'Treatments';
    if (q.id === 'procedures') return 'Procedures';
    return t(q.title);
  };

  const questionToggle = (item) => {
    questionOpenedEvent(item);

    if (!item.collapsed) {
      item.toggle();
      return;
    }

    if (showOnlyAnswered) {
      // Collapse only sibling questions within the same section
      const { parent } = item;
      if (parent?.items?.length) {
        parent.items.forEach((sibling) => {
          if (sibling !== item) sibling.hide();
        });
      }
      item.show();
      if (parent) parent.show();
    } else {
      // Default behavior: collapse all items and open the selected one with its parent
      itemsIterator((i) => { i.hide(); });
      item.show();
      item.parent.show();
    }
  };

  const isLastItem = (item) => {
    if (item.type === INTAKE_SECTIONS.ABOUT_YOU) {
      return true;
    }
    if (item.q) {
      const qs = getIntakeQuestions(user, {
        isShown: true,
        isUnanswered: showOnlyUnanswered,
        customData,
        customQuestions,
        appendQuestions,
        questionsData,
      });
      if (!qs || !qs.length) {
        return false;
      }
      if (qs[qs.length - 1].id === item.q.id) {
        return true;
      }
    }
    return false;
  };

  const renderDoneButton = () => (
    <div className="intake-done-button">
      <Button
        title={t('general.done')}
        action={() => {
          if (gotoResultsAction) {
            gotoResultsAction();
            return;
          }
          app.history.push(ROUTE_PATH[ROUTE_NAME.RESULTS]);
        }}
      />
    </div>
  );

  const AccordionHeader = (item, $el) => {
    render(
      <div
        className="accordion-item-header"
        onClick={item.toggle}
      >
        <div className="flex">
          <div className="accordion-item-collapser">
            <img alt="collapser" src={arrowDownIcon} />
          </div>
          <div className="accordion-item-title">{item.title}</div>
        </div>
        <div>
          <div className="accordion-item-status">{`${item.counter[0]}/${item.counter[1]}`}</div>
        </div>
      </div>,
      $el,
    );
  };

  const QuestionLabel = (item, $el) => {
    if (!item.subTitle) return;

    render(
      <div className="intake-head-label">
        {item.subTitle}
      </div>,
      $el,
    );
  };

  const QuestionHeader = (item, $el) => {
    const isAnswered = isQuestionAnswered(item.q, user);
    const toggle = () => {
      questionToggle(item);
    };
    render(
      isAnswered ? (
        <div className="intake-question-header intake-question-answered" onClick={toggle}>
          <div className="flex">
            <div className="intake-question-header-check">
              <img alt="check" src={checkboxDoneIcon} />
            </div>
            <div className="intake-question-header-title">
              <div className="intake-question-header-subtitle">{item.title}</div>
              <div className="intake-question-header-answer">
                {
                  item?.q?.valueRenderer
                    ? capitalizeFirstLetter(item?.q?.valueRenderer(item.q))
                    : capitalizeFirstLetter(getQuestionValue(item.q))
                }
              </div>
            </div>
          </div>
          <div className="intake-question-header-collapser">
            <img className="collapser_edit" alt="collapser" src={editIcon} />
            <img className="collapser_arrow" alt="collapser" src={arrowDownIcon} />
          </div>
        </div>
      ) : (
        <div className="intake-question-header" onClick={toggle}>
          <div className="intake-question-header-title">
            {item.title}
          </div>
          <div className="intake-question-header-collapser">
            <img alt="collapser" src={arrowDownIcon} />
          </div>
        </div>
      ),
      $el,
    );
  };

  const QuestionBody = (item, $el) => {
    const { q } = item;
    const isAnswered = isQuestionAnswered(q, user);

    render(
      (
        <div data-answered={isAnswered} className={`intake-question ${q?.questionData?.className || ''}`}>
          <div className="intake-question-title question-main-title">{getQuestionTitle(q)}</div>
          <div className="intake-question-collapser">
            <img
              src={arrowDownIcon}
              alt="close"
              onClick={() => {
                item.toggle();
              }}
            />
          </div>
          <QuestionRenderer
            q={q}
            user={user}
            getCustomRenderer={getCustomRenderer}
            initValue={notSureDictionary[q.id] ? CLIENT_IM_NOT_SURE : user?.condition_profile[q.id]}
            resetIntake={resetIntake}
            onselect={(v, preventToggle, preventRender, removeFromNotSure = []) => {
              questionAnsweredEvent(item);
              let newValue = v;

              delete notSureDictionary[q.id];
              removeFromNotSure.forEach((r) => delete notSureDictionary[r]);
              if (newValue === CLIENT_IM_NOT_SURE || (Array.isArray(newValue) && newValue[0] === CLIENT_IM_NOT_SURE)) {
                newValue = null;
                notSureDictionary[q.id] = true;
              }
              localStorage.setItem(CLIENT_IM_NOT_SURE, JSON.stringify(notSureDictionary));

              if (q.setQuestionValue) q.setQuestionValue(newValue, user);
              else if (newValue === null && isArrayQuestion(q)) user.condition_profile[q.id] = [];
              else user.condition_profile[q.id] = newValue;

              if (customOnSelect) {
                customOnSelect(accordionRef.current, item, q, showUnanswered, user, preventRender);
                return;
              }

              app.sendGoogleAnalyticsEvent(GA_INTAKE_CATEGORY, GA_INTAKE_EVENTS.INTAKE_QUESTION_ANSWERED);

              if (!preventToggle) {
                item.$item.classList.add('intake-collapsed');
                setTimeout(() => {
                  // comment
                  item.hide();
                  showUnanswered(q.id);
                  accordionRef.current.render();
                  item.$item.classList.remove('intake-collapsed');
                }, 1000);
              }

              const qstack = getQstack(user, app.questions);
              // !!! user mutation
              user.condition_profile.qstack = qstack;

              if (onUpdate) onUpdate(user);

              if (!intercomUpdated) {
                app.intercom.update({
                  data: {
                    intake_engagement: true,
                  },
                });
              }

              const questions = getIntakeQuestions(user, {
                byType: true,
                isUnanswered: true,
                isShown: true,
                customQuestions,
                customData,
                appendQuestions,
                questionsData,
              });
              const allQuestions = getIntakeQuestions(user, {
                byType: true,
                customQuestions,
                customData,
                appendQuestions,
                questionsData,
              });

              const intercomUpdates = [];

              if (!questions[INTAKE_SECTIONS.ABOUT_YOUR_TREATMENT]?.length) {
                app.sendGoogleAnalyticsEvent(GA_INTAKE_CATEGORY, GA_INTAKE_EVENTS.INTAKE_MEDICAL_DONE);
                intercomUpdates.push('intake_medical_completed');
              }

              let hasMedicalImNotSure = false;
              allQuestions[INTAKE_SECTIONS.ABOUT_YOUR_TREATMENT].forEach(({ id: questionId }) => {
                if (notSureDictionary[questionId]) hasMedicalImNotSure = true;
              });
              if (hasMedicalImNotSure) {
                intercomUpdates.push('intake_medical_im_not_sure');
              }

              if (!questions[INTAKE_SECTIONS.ABOUT_YOU]?.length) {
                app.sendGoogleAnalyticsEvent(GA_INTAKE_CATEGORY, GA_INTAKE_EVENTS.INTAKE_ABOUT_DONE);
                intercomUpdates.push('intake_about_completed');
              }

              if (intercomUpdates.length) {
                const data = {};
                intercomUpdates.forEach((x) => {
                  data[x] = true;
                });
                app.intercom.update({
                  data,
                });
              }
            }}
          />
          {isLastItem(item) ? renderDoneButton() : null}
        </div>
      ),
      $el,
    );
  };

  const intakeClass = classNames('intake', {
    'intake-flat-mode': flatMode,
    [`intake-${id}`]: id,
    [theme]: theme,
  });

  return (
    <div className={intakeClass}>
      { showPreloader ? <Preloader inline height="150px" /> : null }
    </div>
  );
};

Intake.propTypes = {
  id: PropTypes.string,
  user: PropTypes.any,
  section: PropTypes.oneOf(Object.values(INTAKE_SECTIONS)),
  showPreloader: PropTypes.bool,
  flatMode: PropTypes.bool,
  onUpdate: PropTypes.func,
  gotoResultsAction: PropTypes.func,
  qid: PropTypes.string,
  showOnlyUnanswered: PropTypes.bool,
  showOnlyAnswered: PropTypes.any,
  customQuestions: PropTypes.array,
  appendQuestions: PropTypes.array,
  expandQuestions: PropTypes.array,
  customData: PropTypes.object,
  sortSections: PropTypes.array,
  customOnSelect: PropTypes.func,
  questionsData: PropTypes.any,
  onQuestion: PropTypes.any,
  preventNotSure: PropTypes.bool,
  expand: PropTypes.oneOf([
    EXPAND_ALL,
    EXPAND_FIRST,
  ]),
  theme: PropTypes.oneOf(THEMES),
  customizeItems: PropTypes.func,
  onInitialized: PropTypes.func,
  getCustomRenderer: PropTypes.func,
  onAccordionRender: PropTypes.func,
};

export default Intake;
